import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useTheme } from '@/lib/theme';
import { useCourseLang } from '@/lib/courseLang';
import { BookOpen, GraduationCap, ListChecks, Award, Star, ChevronRight, Heart, Share2 } from 'lucide-react';

// Golden course card (#F6DA52), floating on the dark trainee canvas.
const CARD = {
    light: { background: '#F6DA52' },
    dark: { background: '#F6DA52' },
};
const CARD_BORDER = 'border-amber-200 dark:border-brand-yellow/25';

function StatBar({ c, sfx = '' }) {
    const cells = [
        { icon: BookOpen, value: c.lesson_count, label: 'Lessons' },
        { icon: Award, value: c.points_on_complete, label: 'Points' },
        { icon: ListChecks, value: c.quiz_available ? c.question_count : '—', label: 'Quiz' },
    ];
    return (
        <div className="grid grid-cols-3 divide-x divide-brand-navy/10 bg-brand-navy/[0.04] rounded-xl overflow-hidden" data-testid={`course-statbar-${c.id}${sfx}`}>
            {cells.map((s) => (
                <div key={s.label} className="flex flex-col items-center justify-center py-2">
                    <s.icon className="h-4 w-4 text-brand-navy/45 mb-0.5" />
                    <div className="text-base font-black text-brand-black leading-none">{s.value}</div>
                    <div className="text-[13px] font-bold uppercase tracking-wide text-brand-navy/45 mt-0.5">{s.label}</div>
                </div>
            ))}
        </div>
    );
}

function Social({ c, onLike, onShare, className = '', sfx = '' }) {
    return (
        <div className={`flex items-center gap-1.5 ${className}`}>
            <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLike(c.id); }}
                data-testid={`like-btn-${c.id}${sfx}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold transition ${c.liked ? 'bg-rose-50 text-rose-600' : 'bg-brand-navy/5 text-brand-navy/60 hover:bg-brand-navy/10'}`}
                aria-label="Like course">
                <Heart className={`h-3.5 w-3.5 ${c.liked ? 'fill-current' : ''}`} />
                <span data-testid={`like-count-${c.id}${sfx}`}>{c.like_count || 0}</span>
            </button>
            <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare(c); }}
                data-testid={`share-btn-${c.id}${sfx}`}
                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-brand-navy/5 text-brand-navy/60 hover:bg-brand-navy/10 transition"
                aria-label="Share course">
                <Share2 className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}

export default function TraineeCourses() {
    const { theme } = useTheme();
    const cardBg = CARD[theme === 'dark' ? 'dark' : 'light'];
    const [courses, setCourses] = useState(null);
    const [lang] = useCourseLang();
    const load = () => api.get(`/trainee/course-catalog?language=${encodeURIComponent(lang)}`).then((r) => setCourses(r.data || [])).catch(() => setCourses([]));
    useEffect(() => { setCourses(null); load(); }, [lang]);

    const onLike = async (id) => {
        setCourses((prev) => prev.map((c) => c.id === id ? { ...c, liked: !c.liked, like_count: (c.like_count || 0) + (c.liked ? -1 : 1) } : c));
        try {
            const r = await api.post(`/trainee/courses/${id}/like`);
            setCourses((prev) => prev.map((c) => c.id === id ? { ...c, liked: r.data.liked, like_count: r.data.like_count } : c));
        } catch { load(); }
    };

    const onShare = async (c) => {
        const url = `${window.location.origin}/learn/courses/${c.id}`;
        try {
            if (navigator.share) await navigator.share({ title: c.title, text: `Check out "${c.title}" on Propulse`, url });
            else { await navigator.clipboard.writeText(url); toast.success('Course link copied to clipboard'); }
        } catch { /* user dismissed share sheet */ }
    };

    if (!courses) return null;

    return (
        <div data-testid="trainee-courses-page">
            <div className="mb-6">
                <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-white">Topics</h1>
                <p className="text-base text-white/70 font-medium mt-1">Work through the lessons, then pass the quiz to earn your certificate.</p>
            </div>

            {courses.length === 0 && (
                <div className="bg-white rounded-2xl border border-brand-navy/5 p-10 text-center text-brand-navy/40 text-sm shadow-[0_2px_12px_rgba(35,31,32,0.05)]">No courses published yet.</div>
            )}

            {/* Mobile — compact cards */}
            <div className="sm:hidden space-y-3">
                {courses.map((c) => {
                    const certified = !!c.certificate;
                    return (
                        <div key={c.id} className={`rounded-2xl p-3 border ${CARD_BORDER} shadow-[0_2px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.3)]`} style={cardBg} data-testid={`catalog-course-m-${c.id}`}>
                            <Link to={`/learn/courses/${c.id}`} className="flex gap-3.5 items-start">
                                <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden bg-brand-navy/5 relative">
                                    {c.thumbnail_url ? <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-brand-navy/20"><BookOpen className="h-7 w-7" /></div>}
                                    {certified && <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500 text-white">Certified</div>}
                                </div>
                                <div className="flex flex-col flex-1 min-w-0">
                                    <div className="text-[13px] font-bold uppercase tracking-wider text-brand-navy/55">{c.category_label || c.category}</div>
                                    <div className="text-base font-bold text-brand-black leading-tight line-clamp-2 mt-0.5">{c.title}</div>
                                </div>
                            </Link>
                            <div className="mt-2.5"><StatBar c={c} sfx="-m" /></div>
                            <div className="flex items-center justify-between mt-2.5">
                                <Social c={c} onLike={onLike} onShare={onShare} sfx="-m" />
                                <Link to={`/learn/courses/${c.id}`} data-testid={`open-course-m-${c.id}`}
                                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold ${certified ? 'bg-emerald-500 text-white' : 'bg-brand-yellow text-brand-black'}`}>
                                    {certified ? 'Review' : 'Start'} <ChevronRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Desktop — refined grid */}
            <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {courses.map((c) => {
                    const certified = !!c.certificate;
                    return (
                        <div key={c.id} className={`group rounded-2xl border ${CARD_BORDER} overflow-hidden flex flex-col shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_32px_rgba(245,158,11,0.18)] dark:hover:shadow-[0_12px_32px_rgba(255,212,0,0.15)] hover:-translate-y-0.5 transition-all duration-300`} style={cardBg} data-testid={`catalog-course-${c.id}`}>
                            <Link to={`/learn/courses/${c.id}`} className="relative aspect-[16/9] bg-brand-navy/5 block overflow-hidden">
                                {c.thumbnail_url ? <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full grid place-items-center text-brand-navy/20"><BookOpen className="h-8 w-8" /></div>}
                                {certified && <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white inline-flex items-center gap-1 shadow"><GraduationCap className="h-3 w-3" /> Certified</div>}
                                <Social c={c} onLike={onLike} onShare={onShare} className="absolute top-2 right-2 bg-white/85 backdrop-blur-sm rounded-full p-0.5 shadow-sm" />
                            </Link>
                            <div className="p-4 flex-1 flex flex-col">
                                <div className="text-[13px] font-bold uppercase tracking-wider text-brand-navy/55">{c.category_label || c.category}</div>
                                <div className="text-base font-bold tracking-tight text-brand-black mt-1 leading-snug line-clamp-2">{c.title}</div>
                                <div className="mt-3"><StatBar c={c} /></div>
                                {c.best_score > 0 && !certified && (
                                    <div className="mt-2 inline-flex items-center gap-1 text-[13px] text-brand-navy/60 font-medium"><Star className="h-3 w-3 text-brand-yellow fill-current" /> Best {c.best_score}% · pass {c.pass_percent}%</div>
                                )}
                                <div className="mt-auto pt-4">
                                    <Link to={`/learn/courses/${c.id}`}
                                        className={`w-full text-center px-3 py-2.5 rounded-lg text-sm font-bold inline-flex items-center justify-center gap-1.5 transition ${certified ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-brand-yellow text-brand-black hover:brightness-105 hover:shadow-[0_0_15px_rgba(255,212,0,0.4)]'}`}
                                        data-testid={`open-course-${c.id}`}>
                                        {certified ? <><GraduationCap className="h-4 w-4" /> Review course</> : <><BookOpen className="h-4 w-4" /> Start course</>}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
